import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/stores/auth';
import { useThemeStore } from '@/lib/stores/theme';

/**
 * 全局主题同步：仅在「登录 / 切换账号」时把后端 displayPreferences.theme 应用到本地
 * useThemeStore（DOM classList + localStorage）；同一用户后续的 refreshUser（进入
 * 「我的空间」、关注/取关、改资料等触发）**不覆盖**当前主题——保留用户通过顶栏手动
 * 切换的主题。用户主动切换的优先级高于偏好，只有登录时与在设置页改偏好时才会被覆盖。
 *
 * 通过对比 user.id（而非 user 引用）判断是否换了用户身份：
 * - 首次 mount（含页面刷新）：记下当前 uid 即返回，不强制偏好——保留 initTheme 从
 *   localStorage 恢复的主题（即用户最后一次手动选择的）。
 * - uid 从无到有 / 变成另一个 id（登录、切换账号）：应用该用户的主题偏好。
 * - uid 不变（只是 user 对象引用因 refreshUser 而变）：什么都不做。
 */
export function ThemeSync() {
  const user = useAuthStore((s) => s.user);
  // undefined = 尚未初始化（首次 mount）；null = 已知未登录；string = 已登录用户 id
  const lastUserId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const uid = user?.id ?? null;
    if (lastUserId.current === undefined) {
      // 首次 mount（含页面刷新）：保留当前主题，不强制偏好
      lastUserId.current = uid;
      return;
    }
    if (uid !== lastUserId.current) {
      // 用户身份变化（登录 / 切换账号）：应用其主题偏好
      lastUserId.current = uid;
      const pref = user?.displayPreferences?.theme;
      if (pref) {
        useThemeStore.getState().setTheme(pref);
      }
    }
  }, [user]);
  return null;
}
